import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SignoffTerm } from '@testflow/shared';
import { SIGNOFF_TERMS } from '@testflow/shared';
import { useTranslation } from 'react-i18next';
import { projectConfigApi } from '../../../lib/project-config-api.js';
import { useCurrentProject } from '../../../lib/use-project.js';

// Plan 7 / Task F4 (SGN-1-Begriffskonfiguration): der hier gewählte Begriff (Bestätigung|Abnahme)
// speist sowohl die Radio-Labels hier als auch — über dieselben `signoff.bestaetigung`/
// `signoff.abnahme`-i18n-Keys — die Button-/Status-Beschriftung auf der Sign-off-Seite
// (signoff-page.tsx), damit der Begriff überall konsistent ist. Nur zwei sich ausschließende
// Optionen -> Radiogruppe statt Select; sofortiger PATCH bei Auswahl (kein Speichern-Button),
// analog notification-settings.tsx/gamification-panel.tsx.
export function SignoffTermPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;

  const query = useQuery({
    queryKey: ['project-settings', projectId],
    queryFn: () => projectConfigApi.getSettings(projectId ?? ''),
    enabled: Boolean(projectId),
  });

  const patch = useMutation({
    mutationFn: (signoff_term: SignoffTerm) =>
      projectConfigApi.patchSettings(projectId ?? '', { signoff_term }),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
    },
  });

  if (projectLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }
  if (!project || !projectId) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }

  const current = query.data?.signoff_term ?? 'bestaetigung';

  return (
    <div className="space-y-4 p-6">
      <h2 className="text-lg font-bold text-text-primary">{t('admin.terms')}</h2>
      <section className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <p className="text-sm text-text-muted">{t('admin.term.description')}</p>
        <div className="flex flex-col gap-2">
          {SIGNOFF_TERMS.map((term) => (
            <label key={term} className="flex items-center gap-3 text-sm text-text-body">
              <input
                type="radio"
                name="signoff-term"
                checked={current === term}
                disabled={query.isLoading || patch.isPending}
                onChange={() => patch.mutate(term)}
              />
              <span>{t(`signoff.${term}`)}</span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}
